import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useNavigate, useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";
import axios from "axios";
import { RootState } from "../../store/Store";
import {
  FetchDataFailureAction,
  FetchDataSuccessAction,
  fetchData,
} from "../../store/actions";
import { viewActiveUsers, managerAssignmentsUrl, managerAssignmentDetailUrl, projectsUrl } from "../../Service/Apis";
import ManagerAssignmentHistory from "../../components/Employee/ManagerAssignmentHistory";
import { useCustomToast } from "../../components/Methods/SnackBar";
import PageTitle from "../../components/Page Component/PageTitle";

interface UserOption {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  employee_code: string;
  roles: Array<{ role: string } | string>;
  designation?: string | null;
  reporting_manager_id?: string | null;
  reporting_manager_name?: string | null;
}

interface ProjectOption {
  project_id: string;
  project_name: string | null;
}

interface ManagerSectionState {
  existingId: string;
  manager_id: string;
  project_id: string;
  from_date: string;
  till_date: string;
  billable: string;
  assignment_type: string;
}

const BILLABLE_OPTIONS = [
  { value: "yes", label: "Yes" },
  { value: "no", label: "No" },
  { value: "poc", label: "POC" },
  { value: "internal", label: "Internal" },
];

const TYPE_OPTIONS = [
  { value: "qa", label: "QA" },
  { value: "project", label: "Project" },
  { value: "product", label: "Product" },
  { value: "reporting", label: "Reporting" },
];

const emptySection = (): ManagerSectionState => ({
  existingId: "",
  manager_id: "",
  project_id: "",
  from_date: "",
  till_date: "",
  billable: "",
  assignment_type: "",
});

const AssignManager: React.FC = () => {
  const dispatch: ThunkDispatch<
    RootState,
    null,
    FetchDataSuccessAction | FetchDataFailureAction
  > = useDispatch();

  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useCustomToast();

  const [users, setUsers] = useState<UserOption[]>([]);
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>("");
  const [m1, setM1] = useState<ManagerSectionState>(emptySection());
  const [m2, setM2] = useState<ManagerSectionState>(emptySection());
  // Snapshots of the last-saved state — used to detect actual changes
  const [originalM1, setOriginalM1] = useState<ManagerSectionState>(emptySection());
  const [originalM2, setOriginalM2] = useState<ManagerSectionState>(emptySection());
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [userList, projectList] = await Promise.all([
          dispatch(fetchData(viewActiveUsers)),
          dispatch(fetchData(`${projectsUrl}?role=HR`)),
        ]);
        setUsers(userList || []);
        const rawProjects = (projectList || []) as ProjectOption[];
        setProjects(
          [...rawProjects].sort((a, b) =>
            (a.project_name ?? "").localeCompare(b.project_name ?? "", undefined, {
              sensitivity: "base",
            })
          )
        );
          // projectList || []);
      } catch {
        setUsers([]);
        setProjects([]);
      }
    };
    load();
  }, [dispatch]);

  useEffect(() => {
    const preselectedId = (location.state as { preselectedEmployeeId?: string } | null)
      ?.preselectedEmployeeId;
    if (preselectedId && users.length > 0 && !selectedEmployeeId) {
      setSelectedEmployeeId(preselectedId);
    }
  }, [location.state, users]);

  useEffect(() => {
    if (!selectedEmployeeId) {
      setM1(emptySection());
      setM2(emptySection());
      setOriginalM1(emptySection());
      setOriginalM2(emptySection());
      return;
    }
    const fetchAssignments = async () => {
      try {
        const data = await dispatch(
          fetchData(`${managerAssignmentsUrl}?employee_id=${selectedEmployeeId}`)
        );
        const assignments: any[] = Array.isArray(data) ? data : [];
        const m1Assign = assignments.find((a: any) => a.manager_level === "M1" && a.is_active);
        const m2Assign = assignments.find((a: any) => a.manager_level === "M2" && a.is_active);

        const loaded1: ManagerSectionState = m1Assign
          ? {
              existingId: m1Assign.id || "",
              manager_id: m1Assign.manager || "",
              project_id: m1Assign.project || "",
              from_date: m1Assign.from_date || "",
              till_date: m1Assign.till_date || "",
              billable: m1Assign.billable || "",
              assignment_type: m1Assign.assignment_type || "",
            }
          : emptySection();

        const loaded2: ManagerSectionState = m2Assign
          ? {
              existingId: m2Assign.id || "",
              manager_id: m2Assign.manager || "",
              project_id: m2Assign.project || "",
              from_date: m2Assign.from_date || "",
              till_date: m2Assign.till_date || "",
              billable: m2Assign.billable || "",
              assignment_type: m2Assign.assignment_type || "",
            }
          : emptySection();

        setM1(loaded1);
        setM2(loaded2);
        // Store pristine snapshots for change detection
        setOriginalM1(loaded1);
        setOriginalM2(loaded2);
      } catch {
        setM1(emptySection());
        setM2(emptySection());
        setOriginalM1(emptySection());
        setOriginalM2(emptySection());
      }
    };
    fetchAssignments();
  }, [selectedEmployeeId, dispatch]);

  const getRoleName = (r: { role: string } | string) =>
    typeof r === "string" ? r : r?.role || "";

  const managerOptions = users
    .filter((u) =>
      (u.roles || []).some((r) => getRoleName(r).toLowerCase() === "manager")
    )
    .sort((a, b) =>
      `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
    );
  const employeeOptions = [...users].sort((a, b) =>
    `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
  );

  const hasSectionChanged = (
    current: ManagerSectionState,
    original: ManagerSectionState
  ): boolean =>
    current.manager_id !== original.manager_id ||
    current.project_id !== original.project_id ||
    current.from_date !== original.from_date ||
    current.till_date !== original.till_date ||
    current.billable !== original.billable ||
    current.assignment_type !== original.assignment_type;

  const postAssignment = async (
    level: "M1" | "M2",
    section: ManagerSectionState
  ) => {
    const token = localStorage.getItem("token");
    const payload: any = {
      employee: selectedEmployeeId,
      manager: section.manager_id,
      manager_level: level,
      is_active: true,
    };
    if (section.project_id) payload.project = section.project_id;
    if (section.from_date) payload.from_date = section.from_date;
    if (section.till_date) payload.till_date = section.till_date;
    if (section.billable) payload.billable = section.billable;
    if (section.assignment_type) payload.assignment_type = section.assignment_type;

    await axios.post(managerAssignmentsUrl, payload, {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });
  };

  const deactivateAssignment = async (id: string) => {
    const token = localStorage.getItem("token");
    await axios.delete(managerAssignmentDetailUrl(id), {
      headers: { Authorization: `Bearer ${token}` },
    });
  };

  const handleAssign = async () => {
    if (!selectedEmployeeId) {
      showToast("Please select an employee", "error", true);
      return;
    }

    const m1Cleared = !!m1.existingId && !m1.manager_id;
    const m2Cleared = !!m2.existingId && !m2.manager_id;
    const m1Changed = hasSectionChanged(m1, originalM1);
    const m2Changed = hasSectionChanged(m2, originalM2);
    const hasAction = m1Cleared || m2Cleared || m1Changed || m2Changed;

    if (!hasAction) {
      showToast("No changes detected — nothing to save", "info", true);
      return;
    }

    // When M1 and M2 share the same manager, their projects must both be
    // set and must be different — otherwise the KRA system cannot
    // distinguish the two roles and will collapse them into one.
    if (m1.manager_id && m2.manager_id && m1.manager_id === m2.manager_id) {
      if (!m1.project_id || !m2.project_id) {
        showToast(
          "M1 and M2 have the same Manager — a different Project must be selected for each",
          "error",
          true
        );
        return;
      }
      if (m1.project_id === m2.project_id) {
        showToast(
          "M1 and M2 have the same Manager and the same Project — please select different Projects",
          "error",
          true
        );
        return;
      }
    }

    setSubmitting(true);
    try {
      await Promise.all([
        m1Cleared
          ? deactivateAssignment(m1.existingId)
          : m1.manager_id && m1Changed
          ? postAssignment("M1", m1)
          : Promise.resolve(),
        m2Cleared
          ? deactivateAssignment(m2.existingId)
          : m2.manager_id && m2Changed
          ? postAssignment("M2", m2)
          : Promise.resolve(),
      ]);
      showToast("Manager assignment saved successfully", "success", true);
      navigate("/employeeinformation");
    } catch (err: any) {
      const data = err?.response?.data;
      const msg =
        data?.detail ||
        data?.message ||
        (data && typeof data === "object"
          ? Object.values(data).flat().join(", ")
          : "") ||
        "Assignment failed";
      showToast(msg, "error", true);
    } finally {
      setSubmitting(false);
    }
  };

  const renderManagerSection = (
    level: "M1" | "M2",
    state: ManagerSectionState,
    setState: React.Dispatch<React.SetStateAction<ManagerSectionState>>
  ) => (
    <Card variant="outlined" sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
          Reporting Manager {level === "M1" ? 1 : 2}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Manager ({level})</InputLabel>
              <Select
                value={state.manager_id}
                label={`Manager (${level})`}
                onChange={(e) =>
                  setState((prev) => ({ ...prev, manager_id: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {managerOptions.map((m) => (
                  <MenuItem key={m.id} value={m.id}>
                    {m.first_name} {m.last_name} ({m.employee_code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth size="small">
              <InputLabel>Project Name</InputLabel>
              <Select
                value={state.project_id}
                label="Project Name"
                onChange={(e) =>
                  setState((prev) => ({ ...prev, project_id: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {projects.map((p) => (
                  <MenuItem key={p.project_id} value={p.project_id}>
                    {p.project_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="From Date"
              type="date"
              value={state.from_date}
              onChange={(e) =>
                setState((prev) => ({ ...prev, from_date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="To Date"
              type="date"
              value={state.till_date}
              onChange={(e) =>
                setState((prev) => ({ ...prev, till_date: e.target.value }))
              }
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel shrink>Billable</InputLabel>
              <Select
                value={state.billable}
                label="Billable"
                displayEmpty
                notched
                renderValue={(selected) =>
                  selected ? BILLABLE_OPTIONS.find((o) => o.value === selected)?.label ?? selected : "None"
                }
                onChange={(e) =>
                  setState((prev) => ({ ...prev, billable: e.target.value }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {BILLABLE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Manager Type</InputLabel>
              <Select
                value={state.assignment_type}
                label="Manager Type"
                onChange={(e) =>
                  setState((prev) => ({
                    ...prev,
                    assignment_type: e.target.value,
                  }))
                }
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {TYPE_OPTIONS.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ mt: 12, mb: 10, mx: "auto", maxWidth: 900, px: 2 }}>
      <Box sx={{ mb: 3 }}>
        <PageTitle>Assign Manager</PageTitle>
      </Box>

      {/* Employee Information */}
      <Card variant="outlined" sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight="bold" sx={{ mb: 2 }}>
            Employee Information
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Employee Name</InputLabel>
                <Select
                  value={selectedEmployeeId}
                  label="Employee Name"
                  onChange={(e) => setSelectedEmployeeId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Select Employee</em>
                  </MenuItem>
                  {employeeOptions.map((u) => (
                    <MenuItem key={u.id} value={u.id}>
                      {u.first_name} {u.last_name} ({u.employee_code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                size="small"
                label="Designation"
                value={
                  selectedEmployeeId
                    ? users.find((u) => u.id === selectedEmployeeId)?.designation || "—"
                    : ""
                }
                InputProps={{ readOnly: true }}
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      <Divider sx={{ my: 2 }} />

      {/* M1 and M2 sections */}
      {renderManagerSection("M1", m1, setM1)}
      {renderManagerSection("M2", m2, setM2)}

      {/* Actions */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button onClick={() => navigate(-1)}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={submitting || !selectedEmployeeId}
        >
          {submitting ? "Saving..." : "Assign"}
        </Button>
      </Box>

      {/* Assignment history — shown once an employee is selected */}
      {selectedEmployeeId && (() => {
        const emp = users.find((u) => u.id === selectedEmployeeId);
        return (
          <Box sx={{ mt: 4 }}>
            <Divider sx={{ mb: 3 }} />
            <ManagerAssignmentHistory
              employeeId={selectedEmployeeId}
              employeeName={emp ? `${emp.first_name} ${emp.last_name}` : undefined}
            />
          </Box>
        );
      })()}
    </Box>
  );
};

export default AssignManager;
